import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { PermissionService } from '../services/permission.service';
import { MsalRedirectHandlerService } from '../services/msal-redirect-handler.service';
import { map, take, filter, switchMap, timeout, catchError, of } from 'rxjs';

export const permissionGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const permissionService = inject(PermissionService);
  const router = inject(Router);
  const redirectHandler = inject(MsalRedirectHandlerService);
  const requiredPermission: string = route.data['permission'];

  // Si la ruta no requiere ningún permiso específico, se permite el paso libre
  if (!requiredPermission) {
    return true;
  }

  return authService.isInitialized$.pipe(
    filter(initialized => initialized === true),
    take(1),
    switchMap(() => {
      // Si no está autenticado redirigir al login antes de esperar el perfil
      if (!authService.isAuthenticated()) {
        console.log('[PermissionGuard] Usuario no autenticado, redireccionando al login');
        redirectHandler.storeIntendedRoute(state.url);
        authService.loginRedirect();
        return of(false as any);
      }
      // Esperar a que el perfil esté disponible (no capturar null prematuramente)
      return authService.userProfile$.pipe(
        filter(profile => profile !== null),
        take(1),
        timeout({ first: 10000, with: () => of(null) }),
        switchMap(profile => {
          if (!profile) {
            console.warn('[PermissionGuard] Timeout esperando el perfil de usuario');
            return of(router.parseUrl('/unauthorized') as any);
          }

        // Esperamos a que los permisos estén completamente cargados antes de validar para evitar condiciones de carrera (race conditions)
        return permissionService.permissionsLoaded$.pipe(
          filter(loaded => loaded === true),
          take(1),
          switchMap(() => permissionService.userPermissions$.pipe(
            map(() => {
              // Bypass para el Administrador Global de Azure AD
              const isGlobalAdmin = profile.roles.includes('admin');
              const hasPermission = isGlobalAdmin || permissionService.hasPermission(requiredPermission);

              if (!hasPermission) {
                console.warn(`[PermissionGuard] Acceso denegado. Se requiere el permiso lógico: "${requiredPermission}"`);
                return router.parseUrl('/unauthorized');
              }

              return true;
            }),
            take(1)
          ))
        );
        })
      );
    })
  );
};
